import { useState, useMemo, useCallback } from "react";
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useReorderCategories,
  categoriesKeys,
} from "../../queries";
import type { Category, CreateCategoryRequest } from "@/services/api/categories";
import { sortCategories, type SortField } from "./categoryUtils";
import { useCategoriesSearchParams } from "./useCategoriesSearchParams";
import { useQueryClient } from "@tanstack/react-query";

export function useCategoriesPanel() {
  const { data: categories, isLoading } = useCategories();
  const createMutation = useCreateCategory();
  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();
  const reorderMutation = useReorderCategories();
  const queryClient = useQueryClient();

  const { sorting, updateSearchParams } = useCategoriesSearchParams();
  const { sortField, sortDirection } = sorting;

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [deletingCategory, setDeletingCategory] = useState<Category | null>(
    null
  );
  const [attributesCategory, setAttributesCategory] = useState<Category | null>(
    null
  );

  const sortedCategories = useMemo<Category[]>(() => {
    if (!categories) return [];
    return sortCategories(categories, sortField, sortDirection);
  }, [categories, sortField, sortDirection]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      updateSearchParams({
        sortField: field,
        sortDirection: sortDirection === "asc" ? "desc" : "asc",
      });
    } else {
      updateSearchParams({
        sortField: field,
        sortDirection: "asc",
      });
    }
  };

  const handleReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      const reordered = [...sortedCategories];
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);

      const orderItems = reordered.map((cat, index) => ({
        id: Number(cat.id),
        order: index + 1,
      }));

      // Optimistic update: immediately update the cache
      queryClient.setQueryData<Category[]>(
        categoriesKeys.list(),
        (old) => {
          if (!old) return old;
          return old.map((cat) => {
            const item = orderItems.find((o) => o.id === Number(cat.id));
            return item ? { ...cat, order: item.order } : cat;
          });
        }
      );

      reorderMutation.mutate({ categories: orderItems });
    },
    [sortedCategories, reorderMutation, queryClient]
  );

  const handleCreate = (data: CreateCategoryRequest) => {
    if (!data.name) return;

    createMutation.mutate(data, {
      onSuccess: () => {
        setIsCreateDialogOpen(false);
      },
    });
  };

  const handleUpdate = (data: CreateCategoryRequest) => {
    if (!editingCategory) return;

    updateMutation.mutate(
      { id: editingCategory.id, data },
      {
        onSuccess: () => {
          setEditingCategory(null);
        },
      }
    );
  };

  const handleDelete = () => {
    if (!deletingCategory) return;

    deleteMutation.mutate(deletingCategory.id, {
      onSuccess: () => {
        setDeletingCategory(null);
      },
    });
  };

  return {
    categories: sortedCategories,
    isLoading,
    sortField,
    sortDirection,
    isCreateDialogOpen,
    editingCategory,
    deletingCategory,
    attributesCategory,
    handleSort,
    handleReorder,
    handleCreate,
    handleUpdate,
    handleDelete,
    setIsCreateDialogOpen,
    setEditingCategory,
    setDeletingCategory,
    setAttributesCategory,
    createIsLoading: createMutation.isPending,
    updateIsLoading: updateMutation.isPending,
    deleteIsLoading: deleteMutation.isPending,
    categoriesCount: categories?.length,
  };
}
